import { useState } from 'react';
import { createFileRoute, Outlet, Link, redirect } from '@tanstack/react-router';
import { CalendarPlus, Megaphone, CalendarCheck, MessageSquare, CalendarDays, BookOpen, Users } from 'lucide-react';

import { useAuthStore } from '@/stores/authStore';
import { useBatchOverview, useTrainerBatches } from '@/lib/hooks/useTrainer';
import { useBatchCurriculum } from '@/lib/hooks/useLms';
import { computePacing, pacingClasses } from '@/lib/pacing';
import { isPastOrToday } from '@/lib/trainerSessions';
import { useSessionAttendance } from '@/lib/hooks/useSessionAttendance';
import { PreviewAsStudentButton } from '@/components/PreviewAsStudentButton';
import { ScheduleSessionForm } from '@/features/Trainer/ScheduleSessionForm';
import { AnnouncementComposer } from '@/features/Trainer/AnnouncementComposer';
import { AttendanceSheet } from '@/features/Trainer/AttendanceSheet';
import { AttendancePickerDialog } from '@/features/Trainer/AttendancePickerDialog';
import { TrainerMessagesSheet } from '@/features/Trainer/TrainerMessagesSheet';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

export const Route = createFileRoute('/trainer/batches/$batchId')({
  beforeLoad: () => {
    const { isAuthenticated, hasRole } = useAuthStore.getState();
    if (!isAuthenticated) throw redirect({ to: '/login' });
    if (!hasRole('trainer', 'super_admin')) throw redirect({ to: '/dashboard' });
  },
  component: BatchLayout,
});

const TABS = [
  { label: 'Overview', to: '/trainer/batches/$batchId/overview' },
  // Second, not first: the trainer arriving to teach still wants the overview.
  // But it sits ahead of everything else, because "who needs me" is the
  // question they came with on every other visit.
  { label: 'Attention', to: '/trainer/batches/$batchId/attention' },
  { label: 'Sessions', to: '/trainer/batches/$batchId/sessions' },
  { label: 'Grading', to: '/trainer/batches/$batchId/grading' },
  { label: 'Students', to: '/trainer/batches/$batchId/students' },
  { label: 'Curriculum', to: '/trainer/batches/$batchId/curriculum' },
  { label: 'Announcements', to: '/trainer/batches/$batchId/announcements' },
  { label: 'Hackathons', to: '/trainer/batches/$batchId/hackathons' },
] as const;

export function BatchLayout() {
  const { batchId } = Route.useParams();
  const { data: overview } = useBatchOverview(batchId);
  const { data: batches = [] } = useTrainerBatches();
  const { data: curriculum } = useBatchCurriculum(batchId);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [announceOpen, setAnnounceOpen] = useState(false);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [attendancePickerOpen, setAttendancePickerOpen] = useState(false);
  const { attendanceSession, openAttendance, closeAttendance } = useSessionAttendance();

  const trainerBatch = batches.find((b) => b.id === batchId);
  const batchName = overview?.batch?.name ?? trainerBatch?.name ?? '';
  const orgName = trainerBatch?.org_name ?? '';
  const topicCount = curriculum?.modules.flatMap((m) => m.topics).length ?? 0;
  const studentCount = overview?.batch?.student_count ?? trainerBatch?.student_count ?? 0;
  const pendingGrading = overview?.stats?.pending_grading ?? 0;
  const replyNeeded = overview?.stats?.pending_trainer_reply ?? false;
  const unmarkedSessions = (overview?.upcoming_sessions ?? []).filter(
    (s) => isPastOrToday(s.scheduled_at) && !s.attendance_taken,
  );

  const pickSession = (session: (typeof unmarkedSessions)[number]) => {
    setAttendancePickerOpen(false);
    openAttendance({
      id: session.id,
      title: session.title,
      time: new Date(session.scheduled_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
    });
  };

  const openAttendanceEntry = () => {
    if (unmarkedSessions.length === 1) {
      pickSession(unmarkedSessions[0]);
    } else {
      setAttendancePickerOpen(true);
    }
  };

  const pacing = trainerBatch
    ? computePacing(trainerBatch.start_date, trainerBatch.end_date, trainerBatch.progress_pct)
    : null;
  const pacingTone = pacing ? pacingClasses[pacing.pacing] : null;
  const pacingText = pacing
    ? pacing.pacing === 'behind'
      ? `Behind pace by ${Math.round(-pacing.delta)}%`
      : pacing.pacing === 'ahead'
        ? `Ahead of pace by ${Math.round(pacing.delta)}%`
        : null
    : null;

  return (
    <div className="-m-6 flex flex-1 flex-col min-h-0 overflow-y-auto bg-[#faf8f6]">
      <div className="px-6 pt-6">
        {/* Breadcrumb */}
        <nav className="mb-3 flex items-center gap-1.5 text-sm text-[#6b7280]">
          <Link to="/trainer/batches" className="hover:text-[#111827]">Batches</Link>
          <span>›</span>
          <span className="font-medium text-[#111827]">{batchName}</span>
        </nav>

        {/* Header */}
        <div className="mb-2 flex flex-wrap items-center gap-3">
          <h1 className="text-[28px] font-bold leading-tight text-[#111827]">{batchName}</h1>
          {pacingText && pacingTone && (
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${pacingTone.text} bg-[#fff1ec]`}>
              <span className={`h-1.5 w-1.5 rounded-full ${pacingTone.dot}`} />
              {pacingText}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <PreviewAsStudentButton
              batchId={batchId}
              batchName={batchName}
              className="inline-flex items-center gap-1.5 rounded-[12px] border border-[#ececec] bg-white px-3 py-2 text-sm font-medium text-[#111827] hover:bg-[#fafafa]"
            />
            <button
              type="button"
              onClick={openAttendanceEntry}
              className="relative inline-flex items-center gap-1.5 rounded-[12px] border border-[#ececec] bg-white px-3 py-2 text-sm font-medium text-[#111827] hover:bg-[#fafafa]"
            >
              <CalendarCheck className="h-4 w-4" /> Attendance
              {unmarkedSessions.length > 0 && (
                <span data-testid="attendance-badge" className="ml-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#ff5a1f] px-1 text-[10px] font-semibold text-white">
                  {unmarkedSessions.length}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setMessagesOpen(true)}
              className="relative inline-flex items-center gap-1.5 rounded-[12px] border border-[#ececec] bg-white px-3 py-2 text-sm font-medium text-[#111827] hover:bg-[#fafafa]"
            >
              {replyNeeded && (
                <span aria-label="Reply needed" className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[#ff5a1f]" />
              )}
              <MessageSquare className="h-4 w-4" /> Messages
            </button>
            <button
              type="button"
              onClick={() => setAnnounceOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-[12px] border border-[#ececec] bg-white px-3 py-2 text-sm font-medium text-[#111827] hover:bg-[#fafafa]"
            >
              <Megaphone className="h-4 w-4" /> Announce
            </button>
            <button
              type="button"
              onClick={() => setScheduleOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-[12px] bg-[#ff5a1f] px-3.5 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              <CalendarPlus className="h-4 w-4" /> Schedule Session
            </button>
          </div>
        </div>

        {/* Meta strip */}
        <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#6b7280]">
          {orgName && <span>{orgName}</span>}
          {trainerBatch?.start_date && (
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays className="h-4 w-4 text-[#9ca3af]" /> Started {new Date(trainerBatch.start_date).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5">
            <BookOpen className="h-4 w-4 text-[#9ca3af]" /> {topicCount} topic{topicCount === 1 ? '' : 's'}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Users className="h-4 w-4 text-[#9ca3af]" /> {studentCount} student{studentCount === 1 ? '' : 's'}
          </span>
        </div>

        {/* Tab bar */}
        <div className="border-b border-[#ececec]">
          <div className="flex gap-6 overflow-x-auto">
            {TABS.map((tab) => (
              <Link
                key={tab.to}
                to={tab.to}
                params={{ batchId }}
                className="-mb-px flex items-center gap-1.5 whitespace-nowrap border-b-2 border-transparent pb-3 pt-1 text-sm font-medium text-[#6b7280] transition-colors hover:text-[#111827]"
                activeProps={{
                  className: '-mb-px flex items-center gap-1.5 whitespace-nowrap border-b-2 border-[#ff5a1f] pb-3 pt-1 text-sm font-medium text-[#ff5a1f] transition-colors',
                }}
              >
                {tab.label}
                {tab.label === 'Grading' && pendingGrading > 0 && (
                  <span data-testid="grading-tab-badge" className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#ff5a1f] px-1 text-[10px] font-semibold text-white">
                    {pendingGrading}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Tab content */}
      <div className="flex min-h-0 flex-1 flex-col px-6 pb-6 pt-5">
        <Outlet />
      </div>

      {/* Schedule Session modal */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent
          className="max-w-2xl overflow-visible"
          onInteractOutside={(e) => {
            if ((e.target as Element)?.closest?.('[data-radix-popper-content-wrapper]')) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Schedule Session</DialogTitle>
          </DialogHeader>
          <ScheduleSessionForm
            batchId={batchId}
            modules={curriculum?.modules ?? []}
            sessions={overview?.upcoming_sessions ?? []}
            forceOpen
            onSuccess={() => setScheduleOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Announce modal */}
      <Dialog open={announceOpen} onOpenChange={setAnnounceOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Post Announcement</DialogTitle>
          </DialogHeader>
          <AnnouncementComposer
            batchId={batchId}
            onSuccess={() => setAnnounceOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <TrainerMessagesSheet batchId={batchId} open={messagesOpen} onClose={() => setMessagesOpen(false)} />

      <AttendancePickerDialog
        sessions={unmarkedSessions}
        open={attendancePickerOpen}
        onClose={() => setAttendancePickerOpen(false)}
        onPick={pickSession}
      />

      {attendanceSession && (
        <AttendanceSheet
          sessionId={attendanceSession.id}
          sessionTitle={attendanceSession.title}
          sessionTime={attendanceSession.time}
          students={overview?.students ?? []}
          open={!!attendanceSession}
          onClose={closeAttendance}
          batchId={batchId}
        />
      )}
    </div>
  );
}
